<script setup>
import { TabGroup, TabList, TabPanels, Tab } from "@headlessui/vue";
import { router } from "@inertiajs/vue3";
import { onMounted } from "vue";

const {
  selectedTab,
  changeTab,
  tabs = {},
  preserveState = true,
  disableUrlChange = false,
  disableOpenNewTab = false,
  disableDefaultTabChange = false,
  replace = true,
} = defineProps({
  selectedTab: {
    type: Number,
    required: true,
  },
  changeTab: {
    type: Function,
    required: true,
  },
  tabs: {
    type: Object,
    required: false,
  },
  preserveState: {
    type: Boolean,
    required: false,
  },
  disableUrlChange: {
    type: Boolean,
    required: false,
  },
  disableOpenNewTab: {
    type: Boolean,
    required: false,
  },
  disableDefaultTabChange: {
    type: Boolean,
    required: false,
  },
  replace: {
    type: Boolean,
    required: false,
  },
});

function change(value) {
  changeTab(value);

  if (!disableUrlChange && tabs) {
    const url = new URL(window.location.href);
    let tab = Object.keys(tabs)[value];

    if (tab) {
      url.searchParams.set("tab", tab);
    }

    for (let key of url.searchParams.keys()) {
      if (key !== "tab") {
        url.searchParams.delete(key);
      }
    }

    if (replace) {
      router.visit(url.toString(), {
        preserveState: preserveState,
        replace: true,
      });
    } else {
      window.history.replaceState({}, "", url.toString());
    }
  }
}

function openNewTab(event, tab) {
  event.preventDefault();

  if (disableOpenNewTab) {
    return;
  }

  let url = new URL(window.location.href);
  url.searchParams.set("tab", tab);

  window.open(url.toString(), "_blank");
}

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const tab = urlParams.get("tab");

  if (!disableDefaultTabChange && tabs && tab) {
    let value = Object.keys(tabs).findIndex((key) => key === tab);

    if (value) {
      changeTab(value);
    }
  }
});
</script>

<template>
  <TabGroup :selected-index="selectedTab" @change="change">
    <div
      class="mb-4 border-b border-gray-200 text-center text-sm text-gray-500"
    >
      <TabList
        class="-mb-px flex flex-wrap text-center text-sm text-gray-500 0"
      >
        <Tab
          as="template"
          v-slot="{ selected }"
          v-for="(title, tab) in tabs"
          :key="tab"
        >
          <button
            type="button"
            @click.ctrl="(event) => openNewTab(event, tab)"
            @mousedown.middle="(event) => openNewTab(event, tab)"
            class="inline-block cursor-pointer rounded-t border-2 border-l-transparent border-r-transparent border-t-transparent p-4 hover:border-l-transparent hover:border-r-transparent hover:border-t-transparent focus:border-l-blue-500 focus:border-r-blue-500 focus:border-t-blue-500 focus:outline-none"
            :class="{
              'border-blue-600 text-blue-600':
                selected,
              'border-transparent hover:border-gray-300 hover:text-gray-600':
                !selected,
            }"
          >
            {{ title }}
          </button>
        </Tab>
      </TabList>
    </div>

    <TabPanels>
      <slot name="panels" />
    </TabPanels>
  </TabGroup>
</template>
